import { AIRLINE_RULE_CANDIDATES, AIRLINE_SOURCE_POLICY, type AirlineRuleCandidate } from '../content/airline-rules';
import type { DepartureCheck, FlightSegment, Journey, RuleSnapshot, WeatherProfile } from './schema';

export function normalizeCarrierCode(value?: string) {
  return value?.trim().toUpperCase().replace(/\s+/g, '') ?? '';
}

export function ruleCandidatesForSegments(segments: FlightSegment[]): AirlineRuleCandidate[] {
  const operating = new Set(segments.map((segment) => normalizeCarrierCode(segment.operatingCarrierCode)).filter(Boolean));
  const marketing = new Set(segments.map((segment) => normalizeCarrierCode(segment.marketingCarrierCode)).filter(Boolean));
  const exact = AIRLINE_RULE_CANDIDATES.filter((candidate) => operating.has(candidate.carrierCode));
  if (exact.length) return exact;
  const marketed = AIRLINE_RULE_CANDIDATES.filter((candidate) => marketing.has(candidate.carrierCode));
  return marketed.length ? marketed : AIRLINE_RULE_CANDIDATES;
}

export function sourceAgeDays(candidate: AirlineRuleCandidate, now = new Date()) {
  const checked = new Date(`${candidate.sourceCheckedAt}T00:00:00.000Z`);
  return Math.max(0, Math.floor((now.getTime() - checked.getTime()) / 86_400_000));
}

export function isRuleCandidateStale(candidate: AirlineRuleCandidate, now = new Date()) {
  return sourceAgeDays(candidate, now) > AIRLINE_SOURCE_POLICY.maxAgeDays;
}

export function confirmRuleCandidate(candidate: AirlineRuleCandidate, confirmedAt = new Date().toISOString()): RuleSnapshot {
  return {
    candidateId: candidate.id,
    carrierCode: candidate.carrierCode,
    carrierName: candidate.carrierName,
    allowanceG: candidate.cabinAllowanceG,
    mainBag: candidate.mainBag,
    personalItem: candidate.personalItem,
    sourceUrl: candidate.sourceUrl,
    sourceCheckedAt: candidate.sourceCheckedAt,
    confirmedAt,
  };
}

export function validateJourneyDates(journey: Pick<Journey, 'departureDate' | 'returnDate'>) {
  if (!journey.departureDate || !journey.returnDate) return null;
  return journey.returnDate < journey.departureDate ? '돌아오는 날짜는 출발 날짜보다 빠를 수 없어요.' : null;
}

export const WEATHER_LABELS: Record<WeatherProfile, string> = {
  hot: '더운 날씨', mild: '온화함', cold: '추운 날씨', rainy: '비·습기', mixed: '변화 큼',
};

const WEATHER_SUGGESTIONS: Record<WeatherProfile, string[]> = {
  hot: ['IT-008', 'IT-012', 'IT-046', 'IT-051'],
  mild: ['IT-002', 'IT-009', 'IT-044'],
  cold: ['IT-007', 'IT-009', 'IT-042'],
  rainy: ['IT-007', 'IT-044', 'IT-049'],
  mixed: ['IT-002', 'IT-007', 'IT-044'],
};

export function suggestedPresetIds(profile?: WeatherProfile) {
  return profile ? WEATHER_SUGGESTIONS[profile] : [];
}

export function createDepartureChecks(hasRuleSnapshot: boolean): DepartureCheck[] {
  return [
    { id: 'identity', phase: 'week_before', label: '여권·신분증 유효기간 확인', checked: false },
    { id: 'entry', phase: 'week_before', label: '비자·입국 조건과 여행자 보험 확인', checked: false },
    { id: 'connectivity', phase: 'week_before', label: '로밍·eSIM과 현지 결제 수단 준비', checked: false },
    { id: 'airline-rule', phase: 'day_before', label: hasRuleSnapshot ? '운항사·운임 규정을 공식 안내에서 다시 확인' : '운항사·운임의 공식 수하물 규정 확인', checked: false },
    { id: 'security', phase: 'day_before', label: '보조배터리·액체·금지 물품 확인', checked: false },
    { id: 'weight', phase: 'day_before', label: '짐을 넣은 실제 가방 무게 재측정', checked: false },
    { id: 'offline', phase: 'day_before', label: '숙소 주소와 중요 예약을 오프라인 저장', checked: false },
    { id: 'medication', phase: 'leaving', label: '약과 필요한 증빙을 바로 꺼낼 가방에 넣기', checked: false },
    { id: 'home', phase: 'leaving', label: '전원·가스·창문과 쓰레기 확인', checked: false },
    { id: 'essentials', phase: 'leaving', label: '여권·휴대폰·지갑·집 열쇠 최종 확인', checked: false },
    { id: 'boarding', phase: 'airport', label: '탑승권, 터미널과 출발 시각 확인', checked: false },
    { id: 'baggage-drop', phase: 'airport', label: '수하물 위탁 마감과 보안검색 위치 확인', checked: false },
  ];
}

export function completedDepartureChecks(checks: DepartureCheck[] = []) {
  return checks.filter((check) => check.checked).length;
}
